import React from "react";
import { Routes } from "@routes";
import { Link } from "react-router-dom";
import { Check, PhoneCall, Car, Receipt, FileText, Leaf, BarChart2, Award, Scale, Bot, MonitorSmartphone } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../../lib/utils";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

// Animation variants
const animations = {
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  },
  item: {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  },
  cardContainer: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  },
  card: {
    hidden: { y: 30, opacity: 0 },
    visible: (i: number) => ({
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        delay: i * 0.1
      }
    })
  },
  feature: {
    hidden: { opacity: 0, x: -10 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: 0.1 + (i * 0.05),
        duration: 0.3
      }
    })
  }
};

// UI Components
// Badge Component
const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline: "text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> { }

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

// Card Component
interface CardProps extends React.HTMLAttributes<HTMLDivElement> { }

function Card({ className, ...props }: CardProps) {
  return <div className={cn("rounded-lg border bg-card text-card-foreground shadow-sm", className)} {...props} />;
}

function CardHeader({ className, ...props }: CardProps) {
  return <div className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />;
}

function CardTitle({ className, ...props }: CardProps) {
  return <h3 className={cn("text-2xl font-semibold leading-none tracking-tight", className)} {...props} />;
}

function CardDescription({ className, ...props }: CardProps) {
  return <p className={cn("text-sm text-muted-foreground", className)} {...props} />;
}

function CardContent({ className, ...props }: CardProps) {
  return <div className={cn("p-6 pt-0", className)} {...props} />;
}

// Button Component
const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  return <button className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

// Feature component for pricing cards
type FeatureProps = {
  title: string;
  description: string;
  icon: React.ElementType;
  index?: number;
};

const Feature = ({ title, description, icon: Icon, index = 0 }: FeatureProps) => (
  <motion.div
    className="flex flex-row gap-4"
    variants={animations.feature}
    custom={index}
  >
    <Icon className="w-4 h-4 mt-2 text-primary" />
    <div className="flex flex-col">
      <p>{title}</p>
      <p className="text-muted-foreground text-sm">{description}</p>
    </div>
  </motion.div>
);

// Plan type definition
type PricingPlan = {
  name: string;
  description: string;
  price: string;
  currency: string;
  frequency: string;
  setupFee: string;
  features: FeatureProps[];
  buttonText?: string;
  buttonVariant?: "default" | "outline";
  path: string;
  setupFeeLabel?: string;
};

// Reusable PricingCard component
const PricingCard = ({
  name,
  description,
  price,
  currency,
  frequency,
  setupFee,
  features,
  buttonText,
  buttonVariant = "outline",
  path,
  setupFeeLabel
}: PricingPlan) => {
  const { t } = useTranslation();
  const isAllInOne = name === t('pricing.plans.allInOne.name');

  const policyFeature = features.find(feature => feature.title === t('pricing.plans.timeRegistration.features.timePolicy.title'));
  const regularFeatures = policyFeature ? features.filter(feature => feature.title !== t('pricing.plans.timeRegistration.features.timePolicy.title')) : features;

  return (
    <Card className={cn(
      "w-full rounded-md flex flex-col justify-between",
      isAllInOne && "border-primary shadow-lg scale-105 bg-primary/5"
    )}>
      <div>
        <CardHeader>
          <CardTitle>
            <span className="flex flex-row gap-4 items-center font-normal">
              {name}
              {isAllInOne && <Badge variant="default" className="text-white">{t('pricing.plans.allInOne.popular')}</Badge>}
            </span>
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-1">
              <p className="flex flex-row items-center gap-2 text-xl">
                <span className="text-4xl">{price} {currency}</span>
                <span className="text-sm text-muted-foreground">{t('pricing.perMonth')}</span>
              </p>
              <p className="text-base flex items-center gap-1">
                <span className="font-semibold text-primary text-lg">{setupFee} {currency}</span>
                <span className="text-muted-foreground">{setupFeeLabel || t('pricing.setupFee')}</span>
              </p>
            </div>
            <motion.div
              className="flex flex-col gap-4"
              variants={animations.container}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {regularFeatures.map((feature, idx) => (
                <Feature key={idx} index={idx} title={feature.title} description={feature.description} icon={feature.icon} />
              ))}

              {policyFeature && (
                <motion.div
                  className="flex flex-row gap-4"
                  variants={animations.feature}
                  custom={regularFeatures.length}
                >
                  <Check className="w-4 h-4 mt-2 text-primary" />
                  <div className="flex flex-col">
                    <p>{t('pricing.plans.timeRegistration.features.timePolicy.title')}</p>
                    <div className="flex flex-row items-center gap-1 text-sm text-muted-foreground">
                      <span className="font-semibold text-primary">495 {currency}</span>
                      <span>{t('pricing.perContract')}</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </CardContent>
      </div>

      <div className="p-6">
        <Link
          to={path}
          className={cn(
            buttonVariants({ variant: buttonVariant }),
            "gap-4 flex items-center justify-center w-full"
          )}
        >
          {buttonText || t('pricing.bookMeeting')} <PhoneCall className="w-4 h-4" />
        </Link>
      </div>
    </Card>
  );
};

// Main Pricing Component
function Pricing() {
  const { t } = useTranslation();

  // Icons for features
  const icons = {
    Scale,
    Bot,
    MonitorSmartphone,
    Check,
    Car,
    FileText,
    Receipt,
    BarChart2,
    Leaf
  };

  // Define all pricing plans with translation keys
  const pricingPlans: PricingPlan[] = [
    {
      name: t('pricing.plans.timeRegistration.name'),
      description: t('pricing.plans.timeRegistration.description'),
      price: "20",
      currency: "DKK",
      frequency: "month",
      setupFee: "500",
      features: [
        {
          title: t('pricing.plans.timeRegistration.features.legalCompliance.title'),
          description: t('pricing.plans.timeRegistration.features.legalCompliance.description'),
          icon: Scale
        },
        {
          title: t('pricing.plans.timeRegistration.features.automaticTracking.title'),
          description: t('pricing.plans.timeRegistration.features.automaticTracking.description'),
          icon: Bot
        },
        {
          title: t('pricing.plans.timeRegistration.features.userFriendly.title'),
          description: t('pricing.plans.timeRegistration.features.userFriendly.description'),
          icon: MonitorSmartphone
        },
        {
          title: t('pricing.plans.timeRegistration.features.timePolicy.title'),
          description: t('pricing.plans.timeRegistration.features.timePolicy.description'),
          icon: Check
        },
      ],
      path: Routes.ContactSales.path
    },
    {
      name: t('pricing.plans.drivingAllowance.name'),
      description: t('pricing.plans.drivingAllowance.description'),
      price: "20",
      currency: "DKK",
      frequency: "month",
      setupFee: "500",
      features: [
        {
          title: t('pricing.plans.drivingAllowance.features.gpsTracking.title'),
          description: t('pricing.plans.drivingAllowance.features.gpsTracking.description'),
          icon: Car
        },
        {
          title: t('pricing.plans.drivingAllowance.features.taxCompliance.title'),
          description: t('pricing.plans.drivingAllowance.features.taxCompliance.description'),
          icon: FileText
        },
        {
          title: t('pricing.plans.drivingAllowance.features.accounting.title'),
          description: t('pricing.plans.drivingAllowance.features.accounting.description'),
          icon: Receipt
        },
        {
          title: t('pricing.plans.drivingAllowance.features.dashboard.title'),
          description: t('pricing.plans.drivingAllowance.features.dashboard.description'),
          icon: BarChart2
        },
      ],
      path: Routes.ContactSales.path
    },
    {
      name: t('pricing.plans.co2.name'),
      description: t('pricing.plans.co2.description'),
      price: "20",
      currency: "DKK",
      frequency: "month",
      setupFee: "500",
      features: [
        {
          title: t('pricing.plans.co2.features.automatedCalc.title'),
          description: t('pricing.plans.co2.features.automatedCalc.description'),
          icon: Leaf
        },
        {
          title: t('pricing.plans.co2.features.dashboard.title'),
          description: t('pricing.plans.co2.features.dashboard.description'),
          icon: BarChart2
        },
        {
          title: t('pricing.plans.co2.features.esgCompliance.title'),
          description: t('pricing.plans.co2.features.esgCompliance.description'),
          icon: FileText
        },
        {
          title: t('pricing.plans.co2.features.integration.title'),
          description: t('pricing.plans.co2.features.integration.description'),
          icon: MonitorSmartphone
        },
      ],
      path: Routes.ContactSales.path
    },
    {
      name: t('pricing.plans.allInOne.name'),
      description: t('pricing.plans.allInOne.description'),
      price: "40",
      currency: "DKK",
      frequency: "month",
      setupFee: "0",
      features: [
        {
          title: t('pricing.plans.allInOne.features.complete.title'),
          description: t('pricing.plans.allInOne.features.complete.description'),
          icon: Check
        },
        {
          title: t('pricing.plans.allInOne.features.reporting.title'),
          description: t('pricing.plans.allInOne.features.reporting.description'),
          icon: BarChart2
        },
        {
          title: t('pricing.plans.allInOne.features.integration.title'),
          description: t('pricing.plans.allInOne.features.integration.description'),
          icon: MonitorSmartphone
        },
      ],
      path: Routes.ContactSales.path
    }
  ];

  return (
    <div id="pricing" className="bg-secBg w-full py-10 lg:py-40">
      <div className="container mx-auto">
        <motion.div
          className="flex text-center justify-center items-center gap-4 flex-col"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={animations.container}
        >
          <motion.div
            className="flex gap-2 flex-col"
            variants={animations.item}
          >
            <h2 className="text-3xl md:text-5xl tracking-tighter max-w-xl text-center font-regular">
              {t('pricing.subtitle')}
            </h2>
          </motion.div>
          <motion.div
            className="grid pt-20 text-left grid-cols-1 lg:grid-cols-3 max-w-5xl mx-auto gap-8"
            variants={animations.cardContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                custom={index}
                variants={animations.card}
                whileHover={{
                  y: -10,
                  transition: { duration: 0.2 }
                }}
              >
                <PricingCard {...plan} />
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

export { Pricing };
